import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface LeapWordmarkProps {
  size?: 'small' | 'large';
  className?: string;
}

export const LeapWordmark = ({ size = 'small', className }: LeapWordmarkProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const wordmarkRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Size configurations
  const sizeConfig = {
    small: {
      height: 32,
      width: 120,
      fontSize: 32,
      letterSpacing: 2,
    },
    large: {
      height: 56,
      width: 210,
      fontSize: 56,
      letterSpacing: 3,
    },
  };

  const config = sizeConfig[size];

  return (
    <svg
      ref={wordmarkRef}
      width={config.width}
      height={config.height}
      viewBox={`0 0 ${config.width} ${config.height}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Leap"
      className={cn(
        "transition-all duration-600 ease-out",
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95",
        "hover:brightness-110 cursor-default",
        "will-change-transform",
        className
      )}
      style={{
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out, filter 0.2s ease-out",
      }}
    >
      <title>Leap</title>
      
      {/* Light Mode Gradient */}
      <defs>
        <linearGradient id="leap-gradient-light" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: "hsl(172, 100%, 28%)", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "hsl(263, 52%, 32%)", stopOpacity: 1 }} />
        </linearGradient>
        
        {/* Dark Mode Gradient */}
        <linearGradient id="leap-gradient-dark" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: "hsl(172, 100%, 42%)", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "hsl(263, 62%, 50%)", stopOpacity: 1 }} />
        </linearGradient>
      </defs>

      {/* Text with theme-aware gradient */}
      <text
        x="50%"
        y="85%"
        dominantBaseline="alphabetic"
        textAnchor="middle"
        fontFamily="'Clash Display', sans-serif"
        fontWeight="700"
        fontSize={config.fontSize}
        letterSpacing={config.letterSpacing}
        className="fill-[url(#leap-gradient-light)] dark:fill-[url(#leap-gradient-dark)]"
        style={{
          transition: "fill 0.3s ease-out",
        }}
      >
        Leap
      </text>
    </svg>
  );
};
